/**
 * Extract ISO 3166-1 alpha-2 country code from a Mapbox feature
 */
function extractCountryCode(feature) {
  if (!feature) return '';

  if (feature.properties?.short_code && feature.id?.startsWith('country.')) {
    return feature.properties.short_code.toUpperCase();
  }

  const countryContext = feature.context?.find(c => c.id?.startsWith('country.'));
  if (countryContext?.short_code) {
    return countryContext.short_code.toUpperCase();
  }

  return '';
}

/**
 * Extract country long name from a Mapbox feature
 */
function extractCountryName(feature) {
  if (!feature) return '';

  if (feature.id?.startsWith('country.') && feature.text) {
    return feature.text;
  }

  const countryContext = feature.context?.find(c => c.id?.startsWith('country.'));
  if (countryContext?.text) {
    return countryContext.text;
  }

  return '';
}

/**
 * Composable for forward and reverse geocoding using Mapbox
 */
export function useGeocoding(mapboxToken) {
  const forwardGeocode = async (address) => {
    if (!address.trim() || !mapboxToken) {
      return null;
    }

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${mapboxToken}&limit=1`
      );

      const data = await response.json();

      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        const [lng, lat] = feature.center;
        return {
          lat,
          lng,
          address: feature.place_name || address,
          countryCode: extractCountryCode(feature),
          countryName: extractCountryName(feature)
        };
      }
      return null;
    } catch (error) {
      console.error('Error forward geocoding:', error);
      return null;
    }
  };

  const reverseGeocode = async (lat, lng) => {
    if (!mapboxToken) {
      return null;
    }

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxToken}&limit=1`
      );

      const data = await response.json();

      if (data.features && data.features.length > 0 && data.features[0].place_name) {
        const feature = data.features[0];
        return {
          address: feature.place_name,
          countryCode: extractCountryCode(feature),
          countryName: extractCountryName(feature)
        };
      }
      return null;
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      return null;
    }
  };

  return {
    forwardGeocode,
    reverseGeocode,
    extractCountryCode,
    extractCountryName
  };
}

